import React, { useState, useRef } from "react";
import { API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import {
  DollarSign, Shield, Clock, CheckCircle, ChevronDown,
  ArrowRight, Phone, Lock, FileText, User, Mail, MapPin,
  Briefcase, Home as HomeIcon, CreditCard, AlertCircle
} from "lucide-react";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY"
];

const EMPLOYMENT_OPTIONS = ["Employed", "Self-Employed", "Unemployed", "Retired"];
const RESIDENCE_OPTIONS = ["Rent", "Own", "Other"];

const FinancingPage = () => {
  React.useEffect(() => { document.title = "Financing | New Leaf PMC LLC"; }, []);
  const formRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [captchaChecked, setCaptchaChecked] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", address: "", city: "",
    state: "FL", zip_code: "", dob: "", ssn: "",
    monthly_income: "", employment_status: "", residence: "",
    loan_amount: "", loan_purpose: "",
    rep_name: "New Leaf PMC", rep_email: "", rep_company: "New Leaf PMC"
  });

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const updateField = (field, value) => setForm(p => ({ ...p, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captchaChecked) { toast.error("Please confirm the captcha checkbox"); return; }

    const required = ["full_name","email","phone","address","city","state","zip_code","dob","ssn","monthly_income","employment_status","residence","loan_amount"];
    for (const f of required) {
      if (!form[f]?.trim()) {
        toast.error(`${f.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())} is required`);
        return;
      }
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/financing-application`, { ...form, captcha_token: "verified" });
      setSubmitted(true);
      toast.success("Application submitted successfully!");
      setTimeout(() => { window.location.href = "/"; }, 4000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit application");
    } finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0B0C10] pt-24 pb-16 flex items-center justify-center px-4">
        <div className="max-w-lg w-full text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
            <CheckCircle size={40} className="text-green-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">Application Received!</h1>
          <p className="text-[#A0AAB2] text-lg mb-2">
            Your financing application has been submitted successfully.
          </p>
          <p className="text-[#A0AAB2]">A team member will contact you shortly to discuss your options.</p>
          <p className="text-[#A0AAB2]/60 text-sm mt-6">Redirecting to homepage...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0C10]" data-testid="financing-page">
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#4A2B6E]/20 via-[#0D5C63]/10 to-transparent"></div>
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#FF6B35]/10 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-10 right-10 w-72 h-72 bg-[#4A2B6E]/10 rounded-full blur-[120px]"></div>
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center space-x-2 bg-[#FF6B35]/10 border border-[#FF6B35]/30 rounded-full px-4 py-1.5 mb-6">
            <DollarSign size={14} className="text-[#FF6B35]" />
            <span className="text-[#FF6B35] text-sm font-medium">Financing Available</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-testid="financing-hero-title">
            Flexible Financing <br className="hidden md:block" />
            <span className="gradient-text">Options Available</span>
          </h1>
          <p className="text-lg text-[#A0AAB2] mb-8 max-w-2xl mx-auto">
            Get the work you need done now and pay over time with fast approvals.
            No pressure, simple process, quick decisions.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button onClick={scrollToForm}
              className="gradient-btn px-8 py-4 rounded-xl font-bold text-lg flex items-center space-x-2"
              data-testid="apply-now-hero-btn">
              <span>Apply Now</span><ArrowRight size={20} />
            </button>
            <a href="tel:863-340-9244"
              className="flex items-center space-x-2 px-6 py-4 rounded-xl border border-[#2A2B32] text-white hover:bg-white/5 transition-all">
              <Phone size={18} /><span>Call Us: 863-340-9244</span>
            </a>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-12 border-y border-[#2A2B32]/50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Clock, title: "Fast Approvals", desc: "Decisions in minutes" },
              { icon: Shield, title: "Secure & Private", desc: "256-bit encrypted" },
              { icon: CreditCard, title: "Flexible Terms", desc: "Plans that fit your budget" },
              { icon: CheckCircle, title: "No Hidden Fees", desc: "Transparent process" }
            ].map((b, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-[#FF6B35]/10 flex items-center justify-center">
                  <b.icon size={22} className="text-[#FF6B35]" />
                </div>
                <h3 className="text-white font-semibold text-sm">{b.title}</h3>
                <p className="text-[#A0AAB2] text-xs mt-1">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section ref={formRef} className="py-16 md:py-24" id="apply">
        <div className="max-w-3xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">Credit Application</h2>
            <p className="text-[#A0AAB2]">Fill out the form below and we'll get back to you quickly.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="financing-form">
            {/* Personal Information */}
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4 flex items-center space-x-2">
                <User size={16} className="text-[#FF6B35]" /><span>Personal Information</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInput label="Full Name" value={form.full_name} onChange={v => updateField("full_name", v)} required testId="fin-name" />
                <FormInput label="Email Address" type="email" value={form.email} onChange={v => updateField("email", v)} required testId="fin-email" />
                <FormInput label="Phone Number" type="tel" value={form.phone} onChange={v => updateField("phone", v)} required testId="fin-phone" placeholder="(555) 123-4567" />
                <FormInput label="Date of Birth" type="date" value={form.dob} onChange={v => updateField("dob", v)} required testId="fin-dob" />
                <div className="md:col-span-2">
                  <FormInput label="Street Address" value={form.address} onChange={v => updateField("address", v)} required testId="fin-address" />
                </div>
                <FormInput label="City" value={form.city} onChange={v => updateField("city", v)} required testId="fin-city" />
                <div className="grid grid-cols-2 gap-4">
                  <FormSelect label="State" value={form.state} onChange={v => updateField("state", v)} options={US_STATES.map(s => ({ value: s, label: s }))} required testId="fin-state" />
                  <FormInput label="Zip Code" value={form.zip_code} onChange={v => updateField("zip_code", v)} required testId="fin-zip" placeholder="33801" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[#A0AAB2] text-sm mb-1.5 block flex items-center space-x-1">
                    <Lock size={11} /><span>Social Security Number *</span>
                  </label>
                  <input type="password" value={form.ssn} onChange={e => updateField("ssn", e.target.value)}
                    className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white focus:border-[#FF6B35] focus:outline-none transition-colors"
                    placeholder="XXX-XX-XXXX" required data-testid="fin-ssn" autoComplete="off" />
                  <p className="text-[#A0AAB2]/60 text-[10px] mt-1 flex items-center space-x-1">
                    <Shield size={9} /><span>Encrypted & secure. Only the last 4 digits are stored.</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Financial Information */}
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4 flex items-center space-x-2">
                <DollarSign size={16} className="text-[#FF6B35]" /><span>Financial Information</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInput label="Monthly Income ($)" type="number" value={form.monthly_income} onChange={v => updateField("monthly_income", v)} required testId="fin-income" placeholder="5000" />
                <FormSelect label="Employment Status" value={form.employment_status} onChange={v => updateField("employment_status", v)}
                  options={EMPLOYMENT_OPTIONS.map(o => ({ value: o, label: o }))} required testId="fin-employment" placeholder="Select..." />
                <FormSelect label="Residence" value={form.residence} onChange={v => updateField("residence", v)}
                  options={RESIDENCE_OPTIONS.map(o => ({ value: o, label: o }))} required testId="fin-residence" placeholder="Select..." />
                <FormInput label="Loan Amount Requested ($)" type="number" value={form.loan_amount} onChange={v => updateField("loan_amount", v)} required testId="fin-loan-amount" placeholder="10000" />
                <div className="md:col-span-2">
                  <FormInput label="Loan Purpose" value={form.loan_purpose} onChange={v => updateField("loan_purpose", v)} testId="fin-purpose" placeholder="Home renovation, kitchen remodel, etc." />
                </div>
              </div>
            </div>

            {/* Captcha */}
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
              <label className="flex items-center space-x-3 cursor-pointer" data-testid="fin-captcha">
                <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                  captchaChecked ? "bg-[#FF6B35] border-[#FF6B35]" : "border-[#2A2B32] bg-[#1F2027]"
                }`} onClick={() => setCaptchaChecked(!captchaChecked)}>
                  {captchaChecked && <CheckCircle size={14} className="text-white" />}
                </div>
                <span className="text-[#A0AAB2] text-sm">I'm not a robot</span>
              </label>
            </div>

            {/* Submit */}
            <button type="submit" disabled={submitting}
              className="w-full gradient-btn py-4 rounded-xl font-bold text-lg flex items-center justify-center space-x-2 disabled:opacity-50"
              data-testid="submit-financing-btn">
              {submitting ? (
                <><div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div><span>Submitting...</span></>
              ) : (
                <><FileText size={20} /><span>Submit Application</span></>
              )}
            </button>

            <p className="text-[#A0AAB2]/60 text-xs text-center">
              By submitting this application, you authorize New Leaf PMC LLC and its financing partners to review your information.
              Your data is encrypted and handled securely.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
};

/* ===== Reusable Form Components ===== */
const FormInput = ({ label, type = "text", value, onChange, required, testId, placeholder }) => (
  <div>
    <label className="text-[#A0AAB2] text-sm mb-1.5 block">{label} {required && "*"}</label>
    <input type={type} value={value} onChange={e => onChange(e.target.value)}
      className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white focus:border-[#FF6B35] focus:outline-none transition-colors"
      placeholder={placeholder} required={required} data-testid={testId} />
  </div>
);

const FormSelect = ({ label, value, onChange, options, required, testId, placeholder }) => (
  <div>
    <label className="text-[#A0AAB2] text-sm mb-1.5 block">{label} {required && "*"}</label>
    <select value={value} onChange={e => onChange(e.target.value)}
      className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white focus:border-[#FF6B35] focus:outline-none transition-colors appearance-none"
      required={required} data-testid={testId}>
      <option value="">{placeholder || "Select..."}</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

export default FinancingPage;
